import { createFileRoute, Link } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { 
  XCircle, 
  ExternalLink,
  Users,
  MessageCircle,
  Filter,
  Star,
  Loader2
} from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import logoAsset from "@/assets/logo.png.asset.json";

export const Route = createFileRoute("/acesso-negado")({
  component: AcessoNegadoRoute,
});

function AcessoNegadoRoute() {
  return <AcessoNegado />;
}

function AcessoNegado() {
  const { user, refreshProfile } = useAuth();
  const [showAltForm, setShowAltForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [altEmail, setAltEmail] = useState("");

  const handleAltEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!altEmail) return;
    
    setLoading(true);
    const { error } = await supabase
        .from('profiles')
        .update({ 
            email_gopage_alternativo: altEmail,
            status: 'pendente' 
        })
        .eq('id', user?.id);

    if (error) {
        toast.error("Erro ao atualizar cadastro.");
        setLoading(false);
        return;
    }

    toast.success("Cadastro enviado para revisão!");
    await refreshProfile();
    setLoading(false);
  };

  const benefits = [
    { icon: <Users className="w-5 h-5" />, text: "Acesso a fornecedores de todo o Brasil" },
    { icon: <MessageCircle className="w-5 h-5" />, text: "Contato direto por WhatsApp" },
    { icon: <Filter className="w-5 h-5" />, text: "Filtro por segmento, cidade e polo atacadista" },
    { icon: <Star className="w-5 h-5" />, text: "Sua lista de fornecedores favoritos" },
  ];

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-white">
      {/* Painel Esquerdo */}
      <div className="flex md:flex-col justify-between p-6 md:p-12 bg-[#0C1A12] text-white w-full md:w-1/3 min-h-[100px] md:min-h-screen">
        <Link to="/">
          <img src={logoAsset.url} alt="Gopage" className="h-10 w-10 md:h-12 md:w-12 rounded-xl" />
        </Link>
        <div className="hidden md:block">
          <h2 className="text-4xl font-bold mb-6 leading-tight">Acesso restrito.</h2>
          <p className="text-lg opacity-80 leading-relaxed">
            Faça login para acessar os contatos diretos de fornecedores e atacadistas de todo o Brasil.
          </p>
        </div>
        <div className="hidden md:block text-sm opacity-40">© 2026 Gopage</div>
      </div>

      <main className="flex-1 flex items-center justify-center p-6 overflow-y-auto">
          <div className="max-w-2xl w-full">
              <div className="text-center mb-12">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-destructive/10 rounded-full mb-6">
                      <XCircle className="w-10 h-10 text-destructive" />
                  </div>
                  <h1 className="text-4xl font-bold mb-4 text-[#0C1A12]">A Lista de Fornecedores é exclusiva para clientes Gopage</h1>
                  <p className="text-xl text-muted-foreground">
                      Não encontramos o e-mail <span className="font-bold text-[#0C1A12]">{user?.email}</span> entre as contas Gopage.
                  </p>
              </div>

              <div className="grid md:grid-cols-2 gap-12 mb-12 items-center">
                  <div className="space-y-6">
                      <h2 className="text-lg font-bold text-[#0C1A12] uppercase tracking-widest">O que você está perdendo:</h2>
                      <ul className="space-y-4">
                          {benefits.map((benefit, i) => (
                              <li key={i} className="flex gap-3 items-center text-muted-foreground">
                                  <div className="bg-[#F7F9F5] p-2 rounded-lg text-[#0C1A12]">
                                      {benefit.icon}
                                  </div>
                                  <span className="font-medium">{benefit.text}</span>
                              </li>
                          ))}
                      </ul>
                  </div>

                  <div className="bg-[#F7F9F5] rounded-3xl p-8 border border-border shadow-sm">
                      <div className="text-center mb-6">
                          <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest block mb-2">Planos a partir de</span>
                          <div className="text-4xl font-bold text-[#0C1A12]">R$ 39,90<span className="text-base font-normal text-muted-foreground">/mês</span></div>
                      </div>
                      
                      <Button asChild size="lg" variant="onLight" className="w-full h-[52px] rounded-full shadow-lg transition-all mb-4 group">
                        <a 
                            href="https://gopage.bio?utm_source=lista-fornecedores&utm_medium=acesso-negado&utm_campaign=mvp-diretorio" 
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Criar minha conta Gopage
                            <ExternalLink className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                        </a>
                      </Button>
                      
                      <p className="text-[10px] text-center text-muted-foreground">
                          Aprovação instantânea no diretório após a assinatura.
                      </p>
                  </div>
              </div>

              {!showAltForm ? (
                  <div className="text-center pt-8 border-t border-border">
                      <button 
                          onClick={() => setShowAltForm(true)}
                          className="text-[#0C1A12] font-bold hover:underline transition-all text-sm md:text-base"
                      >
                          Já sou cliente com outro e-mail — quero revisar meu cadastro
                      </button>
                  </div>
              ) : (
                  <div className="bg-white border border-border p-8 rounded-3xl animate-in slide-in-from-bottom-4 duration-500">
                      <h3 className="text-lg font-bold mb-4">Informe o e-mail da sua conta Gopage</h3>
                      <form onSubmit={handleAltEmailSubmit} className="space-y-4">
                          <input 
                              type="email"
                              required
                              value={altEmail}
                              onChange={(e) => setAltEmail(e.target.value)}
                              className="w-full px-4 py-4 bg-[#F7F9F5] border border-border rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#C6F764]"
                              placeholder="seu-email-gopage@exemplo.com"
                          />
                          <div className="flex flex-col md:flex-row gap-2">
                              <Button 
                                  disabled={loading}
                                  variant="onLight"
                                  className="flex-1 py-7 font-bold rounded-2xl transition-all"
                              >
                                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enviar para Revisão"}
                              </Button>
                              <Button 
                                  type="button"
                                  onClick={() => setShowAltForm(false)}
                                  variant="outlineDark"
                                  className="px-6 py-7 rounded-2xl font-bold transition-all"
                              >
                                  Cancelar
                              </Button>
                          </div>
                      </form>
                  </div>
              )}
          </div>
      </main>
    </div>
  );
}
